import { isErrorWithMessage } from '../utils/typeGuards';

import type { ErrorPattern } from './patterns';

/**
 * 에러 필터 설정
 */
export interface ErrorFilterConfig {
  /**
   * 무시할 에러 패턴들
   * - string: 에러 메시지에 포함된 문자열
   * - RegExp: 에러 메시지 정규식 패턴
   * - function: 커스텀 에러 체크 함수
   */
  patterns: readonly ErrorPattern[];
}

/**
 * 에러에서 메시지 문자열 추출
 * - Error 객체: message 속성
 * - DOMException: "name: message" 형태로 결합 (name과 message가 분리되어 있으므로)
 *   예: DOMException { name: 'InvalidStateError', message: 'Cannot suspend a closed AudioContext.' }
 *   → 'InvalidStateError: Cannot suspend a closed AudioContext.'
 * - 문자열: 그대로 사용 (Non-Error promise rejection 케이스)
 */
const getErrorMessage = (error: unknown): string | null => {
  if (typeof error === 'string') {
    return error;
  }
  if (isErrorWithMessage(error)) {
    // DOMException 등 name이 있는 에러는 "name: message" 형태로 결합
    // Sentry UI에서 표시되는 형식과 일치시켜 패턴 매칭이 정확히 동작하도록 함
    const name = (error as { name?: string }).name;
    if (name && name !== 'Error' && !error.message.includes(name)) {
      return `${name}: ${error.message}`;
    }
    return error.message;
  }
  return null;
};

/**
 * Sentry 이벤트에서 에러 메시지 후보를 추출한다.
 *
 * `window.onerror` 로 잡히는 주입 스크립트/크로스 오리진 에러는
 * `hint.originalException` 이 `null` 인 경우가 흔하다. 이때 `getErrorMessage`는
 * `null` 을 반환해 문자열/정규식 패턴이 **존재해도 매칭되지 않는다**.
 * (예: `zaloJSV2 is not defined` 는 패턴에 있는데도 필터를 통과 → 노이즈 유입)
 *
 * 이 함수는 그 fallback 으로, 이벤트의 `exception.values[].value`(체인된
 * cause 포함)와 `message` 를 합쳐 패턴 매칭이 동작하도록 한다.
 * 포맷은 `getErrorMessage` 와 동일하게 `type !== 'Error'` 이면 `"type: value"` 로 결합.
 */
export const getEventErrorMessage = (event: {
  exception?: { values?: ReadonlyArray<{ type?: string; value?: string }> };
  message?: string;
}): string | null => {
  const parts: string[] = [];
  const values = event?.exception?.values;
  if (values) {
    for (const v of values) {
      const type = v?.type;
      if (!v?.value) {
        // type-only 이벤트({ type: 'ChunkLoadError', value 없음 })에서 type 을
        // 버리면 필터/fingerprint 매칭 표면에서 완전히 사라진다 — type 만이라도 남긴다.
        // 단, 범용 'Error' 는 정보가 없어 제외 (매칭 오염 방지).
        if (type && type !== 'Error') {
          parts.push(type);
        }
        continue;
      }
      parts.push(
        type && type !== 'Error' && !v.value.includes(type)
          ? `${type}: ${v.value}`
          : v.value,
      );
    }
  }
  if (typeof event?.message === 'string' && event.message) {
    parts.push(event.message);
  }
  return parts.length > 0 ? parts.join('\n') : null;
};

/**
 * 에러가 패턴과 매칭되는지 확인
 */
const matchesPattern = (error: unknown, pattern: ErrorPattern): boolean => {
  // 함수형 패턴 (커스텀 체크)
  if (typeof pattern === 'function') {
    return pattern(error);
  }

  // 문자열/정규식은 메시지 기반 매칭
  const message = getErrorMessage(error);
  if (message === null) {
    return false;
  }

  if (typeof pattern === 'string') {
    return message.includes(pattern);
  }

  if (pattern instanceof RegExp) {
    return pattern.test(message);
  }

  return false;
};

/**
 * 에러 필터 함수를 생성합니다.
 *
 * @example
 * ```typescript
 * import { createErrorFilter, CORE_ERROR_PATTERNS, PHASER_ERROR_PATTERNS } from '@zep-us/sentry';
 * import { isWhaleBrowser } from '@zep/utils/device';
 *
 * const shouldIgnoreError = createErrorFilter({
 *   patterns: [
 *     ...CORE_ERROR_PATTERNS,
 *     ...PHASER_ERROR_PATTERNS,
 *     'Custom error message',
 *     // 함수도 patterns에 바로 포함 가능
 *     (error) => isWhaleBrowser() &&
 *                isErrorWithMessage(error) &&
 *                error.message.includes('Blocked a frame with origin'),
 *   ],
 * });
 *
 * if (shouldIgnoreError(error)) {
 *   return; // Sentry로 전송하지 않음
 * }
 * ```
 */
export const createErrorFilter = (config: ErrorFilterConfig) => {
  return (error: unknown): boolean => {
    return config.patterns.some(pattern => matchesPattern(error, pattern));
  };
};

/**
 * 간단한 헬퍼: 패턴 배열만으로 필터 생성
 *
 * @example
 * ```typescript
 * const shouldIgnoreError = createSimpleErrorFilter([
 *   ...CORE_ERROR_PATTERNS,
 *   'Custom error',
 * ]);
 * ```
 */
export const createSimpleErrorFilter = (patterns: readonly ErrorPattern[]) => {
  return createErrorFilter({ patterns });
};
